/**
 * iapCredit.ts — M134. How long the store app waits for bought Shop Points,
 * and what counts as "they arrived". PURE: no imports, unit-tested.
 * M135 added the instant check (further down): the wait below is what is
 * left when that check cannot credit the purchase by itself.
 *
 * Inside the store app Apple / Google take the payment and RevenueCat's
 * webhook credits the wallet (supabase/functions/revenuecat-webhook).
 * RevenueCat documents that delivery as "usually within 5 to 60 seconds".
 * The M133 wait covered 22 s and then closed the sheet without a word, so a
 * player (or an App Review tester) in the slow half had paid and saw nothing.
 *
 * TWO PHASES:
 *   FOREGROUND — the GET SP sheet stays busy and says the payment arrived.
 *                Short on purpose: a screen must not be held for a minute.
 *   BACKGROUND — the sheet is released with a plain "on the way" line and the
 *                wallet keeps being pulled. The +N SP toast fires whenever
 *                the credit lands, on whatever screen the player is on.
 */
export const IAP_FOREGROUND_WAITS_MS: readonly number[] = [1000, 1500, 2500, 3500, 5000];
export const IAP_BACKGROUND_WAITS_MS: readonly number[] = [
  5000, 8000, 10000, 15000, 20000, 30000, 45000, 60000,
];

export function totalWaitMs(waits: readonly number[]): number {
  return waits.reduce((a, b) => a + b, 0);
}

/** What the sheet says once the payment is in and the points are not yet. */
export const SP_ON_THE_WAY =
  'Payment received. Your Shop Points are on the way and will show here in a moment.';

/**
 * What one run of the store's purchase sheet came to.
 *   paid      — paid, and RevenueCat has the receipt. Carries APPLE's own
 *               transaction number when there is one to check (M135), else
 *               null: the credit then arrives by the webhook alone.
 *   cancelled — the player closed the sheet.
 *   error     — the STORE refused before any payment. Safe to say so.
 *   unsure    — the payment MAY have been taken (the confirmation failed on
 *               the way back, or a parent has to approve it). The app keeps
 *               looking for the credit and never claims nothing was charged.
 */
export type StoreBuyFailure = 'cancelled' | { error: string } | { unsure: string };
export type StoreBuyOutcome = { paid: string | null } | StoreBuyFailure;

/**
 * M135 — the instant check. Right after a payment the app asks our server
 * (`claim-iap`) to confirm the purchase with Apple and credit it, so the
 * points land in a second or two instead of waiting for the webhook.
 *
 * It can only ever make things FASTER: anything but a clear "credited" and
 * the app keeps waiting for the webhook exactly as before.
 */
export const IAP_CLAIM_TRIES = 3;
/** One try is cut off after this long... */
export const IAP_CLAIM_TIMEOUT_MS = 4000;
/** ...and all the tries together may use this much, so a server that hangs
 *  can add at most this to the time the GET SP sheet stays busy. */
export const IAP_CLAIM_BUDGET_MS = 8000;

/** The store's transaction id, when it is one our server can check: Apple's
 *  are plain numbers. Google's order ids are not, and are left to the webhook. */
export function claimableTxnId(platform: 'ios' | 'android' | null, raw: unknown): string | null {
  return platform === 'ios' && typeof raw === 'string' && /^[0-9]{1,24}$/.test(raw) ? raw : null;
}

/**
 *   credited — the points are in the wallet; pull it.
 *   retry    — Apple or our server was not ready; worth asking again.
 *   stop     — asking again cannot help (not set up, or not this account's).
 */
export type ClaimVerdict = 'credited' | 'retry' | 'stop';

const CLAIM_RETRY_WHY = ['apple_not_found', 'apple_busy', 'server_busy'];

export function claimVerdict(data: unknown): ClaimVerdict {
  const d = data && typeof data === 'object' ? (data as { status?: unknown; why?: unknown }) : null;
  if (d?.status === 'credited') return 'credited';
  if (d?.status === 'later' && typeof d.why === 'string' && CLAIM_RETRY_WHY.includes(d.why)) {
    return 'retry';
  }
  return 'stop';
}

/**
 * Turn a RevenueCat SDK error into what the player is told.
 *
 * THE RULE: "Nothing was charged" is only ever said when the store itself
 * turned the purchase down. M133 said it for EVERY error, including the ones
 * raised after Apple had taken the payment (the receipt could not be posted:
 * no signal, a server hiccup). The SDK retries those by itself and the points
 * arrive later, so the honest line is "we could not confirm it yet".
 *
 * Codes are RevenueCat's PURCHASES_ERROR_CODE values (strings).
 */
export function outcomeForStoreError(code: string, userCancelled: boolean): StoreBuyFailure {
  if (userCancelled || code === '1') return 'cancelled';
  // 3 not allowed · 4 invalid · 5 not available · 6 already purchased ·
  // 18 ineligible · 32 the product request timed out (nothing was started).
  if (['3', '4', '5', '6', '18', '32'].includes(code)) {
    return { error: 'The store declined the purchase. Nothing was charged.' };
  }
  if (code === '15') return { error: 'A purchase is already in progress.' };
  // 20 = payment pending: Ask to Buy, or the bank wants a confirmation.
  if (code === '20') {
    return {
      unsure: 'Waiting for approval. Your Shop Points arrive as soon as the purchase is approved.',
    };
  }
  return {
    unsure:
      'We could not confirm the purchase yet. If the store charged you, your Shop Points will arrive on their own.',
  };
}

/**
 * The "say so once" latch for bought Shop Points.
 *   before — the purchased total when the purchase began.
 *   owed   — store app only: Shop Points PAID FOR and not seen yet. Two packs
 *            bought back to back (exactly what a store reviewer does) can be
 *            credited by two separate syncs; while anything is still owed the
 *            latch stays armed so the second credit is announced too. The web
 *            return leaves it undefined: one checkout, one credit.
 */
export interface SpCreditLatch {
  pack: string;
  before: number;
  owed?: number;
}

/** A sync adopted `now` purchased points: what to announce, and what stays armed. */
export function settleSpCredit(
  latch: SpCreditLatch | null,
  now: number,
): { gained: number; latch: SpCreditLatch | null } {
  if (!latch || now <= latch.before) return { gained: 0, latch };
  const gained = now - latch.before;
  const owed = (latch.owed ?? 0) - gained;
  return { gained, latch: owed > 0 ? { ...latch, before: now, owed } : null };
}
